import React, { useEffect, useState, useMemo } from "react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import IntroSection from "./IntroSection";
import VideoSourcePicker from "./VideoSourcePicker";
// Import the per-page fallback INTRO constants so the admin form
// pre-fills with the SAME copy members see when no DB override
// exists yet. Before this, `overrides[selected]` was undefined for
// intros the admin had never customised and the form blanked out —
// making admins think the intro had no content when in fact the
// live page was rendering these defaults. Now the form mirrors
// what members see on first mount and Save simply upserts the
// override; Reset restores this same default automatically.
import { WELCOME_INTRO }   from "../pages/Welcome";
import { COURSE_INTRO }    from "../pages/Course";
import { TOOLS_INTRO }     from "../pages/Tools";
import { DATABASES_INTRO } from "../pages/DatabasesIndex";
import { BLUEPRINT_INTRO } from "../pages/LaunchBlueprint";
// Hardcoded section-tour defaults — pulled in so the admin editor
// mirrors exactly what founders see on the live walkthrough when
// no DB override exists yet. Without this, the editor rendered
// "Steps · 0" while the live tour kept showing the compiled-in
// hardcoded steps, confusing admins who thought the tour was empty.
import { SECTION_TOURS } from "./GuidedTour";
import {
  Save, Loader2, Video, RefreshCw, Plus, X, Trash2, GripVertical, Upload,
  Target as TargetIcon, ExternalLink, Sparkles, Compass, TrendingDown, BarChart3,
  Eye, EyeOff, ShieldAlert, ArrowRight,
} from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════
// Intros — admin editor for the three IntroSection blocks
// ═══════════════════════════════════════════════════════════════════════

const INTRO_KEYS = [
  { key: "welcome",    label: "Welcome Overview",       path: "/welcome",   hint: "The Welcome page hero + intro video shown to new members" },
  { key: "course",     label: "Course Overview Intro",  path: "/course",    hint: "Above the module grid on the Course page" },
  { key: "tools",      label: "Financial Tools Intro",  path: "/tools",     hint: "Above the Financial Tools calculators" },
  { key: "databases",  label: "Databases Overview",     path: "/databases", hint: "Above the Institute Databases grid" },
  { key: "blueprint",  label: "Launch Blueprint Intro", path: "/blueprint", hint: "Above the Blueprint coach" },
];

const BLANK_INTRO = { eyebrow: "", title: "", body: "", bullets: [], videoUrl: "" };

/**
 * Frontend fallback copy per intro key. Keyed by the same `key` used in
 * `INTRO_KEYS` above so the admin form can pre-fill with the exact
 * content members see when no DB override exists. Reset button restores
 * the same shape via `POST /admin/intros/{key}/reset` → the admin form
 * then re-reads from these constants.
 *
 * If you add a new intro key, add its default here too — otherwise the
 * form will blank on first open (the original bug).
 */
const INTRO_DEFAULTS = {
  welcome:   WELCOME_INTRO,
  course:    COURSE_INTRO,
  tools:     TOOLS_INTRO,
  databases: DATABASES_INTRO,
  blueprint: BLUEPRINT_INTRO,
};

function defaultsForIntro(key) {
  const d = INTRO_DEFAULTS[key];
  if (!d) return BLANK_INTRO;
  return {
    eyebrow: d.eyebrow || "",
    title:   d.title   || "",
    body:    d.body    || "",
    bullets: Array.isArray(d.bullets) ? d.bullets : [],
    videoUrl: d.videoUrl || "",
  };
}

export function IntrosConsole() {
  const [selected, setSelected] = useState("welcome");
  const [overrides, setOverrides] = useState({});
  const [form, setForm] = useState(BLANK_INTRO);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoStats, setVideoStats] = useState({});
  const videoInputRef = React.useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/intros")
      .then((r) => setOverrides(r.data?.overrides || {}))
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
    // Fetch aggregate video-progress stats — used to render engagement pills
    // on each intro card. We match by convention: {intro_key}_intro.
    api.get("/admin/video-progress")
      .then((r) => setVideoStats(r.data || {}))
      .catch(() => {});
  };
  useEffect(load, []);

  useEffect(() => {
    const doc = overrides[selected];
    if (doc) {
      // Override exists in DB — hydrate the form fully from it, but
      // fall back to the frontend default for any field the admin
      // hasn't overridden yet (e.g. left `body` empty on save because
      // they only wanted to change the title). This makes partial
      // overrides feel like edits rather than resets.
      const d = defaultsForIntro(selected);
      setForm({
        eyebrow:  doc.eyebrow || d.eyebrow,
        title:    doc.title   || d.title,
        body:     doc.body    || d.body,
        bullets:  Array.isArray(doc.bullets) && doc.bullets.length ? doc.bullets : d.bullets,
        videoUrl: doc.videoUrl || d.videoUrl,
      });
    } else {
      // No override yet — pre-fill with the member-facing default so
      // the form matches what's actually rendered on the live page.
      setForm(defaultsForIntro(selected));
    }
  }, [selected, overrides]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/intros/${selected}`, form);
      toast.success("Intro saved");
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Delete this override and fall back to the hardcoded default?")) return;
    try {
      await api.post(`/admin/intros/${selected}/reset`);
      toast.success("Override cleared");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const onPickVideo = () => videoInputRef.current?.click();
  const onUploadVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(mp4|webm|mov)$/i.test(file.name)) {
      toast.error("Video must be .mp4, .webm, or .mov");
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      toast.error("Video is too large (500 MB max)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/intros/${selected}/upload-video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Update the form + overrides so preview + Custom pill refresh
      // without a full reload. Save endpoint has already persisted the
      // storage_path + URL, so we don't call PUT here.
      setForm((prev) => ({ ...prev, videoUrl: r.data.url }));
      toast.success("Video uploaded — replaces the previous file");
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); }
  };

  const setBullet = (i, val) => {
    const next = [...(form.bullets || [])]; next[i] = val;
    setForm({ ...form, bullets: next });
  };
  const addBullet = () => setForm({ ...form, bullets: [...(form.bullets || []), ""] });
  const rmBullet = (i) => {
    const next = [...(form.bullets || [])]; next.splice(i, 1);
    setForm({ ...form, bullets: next });
  };

  const currentMeta = INTRO_KEYS.find((k) => k.key === selected);

  return (
    <div data-testid="admin-intros-console">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> CMS
          </p>
          <h2 className="font-display text-2xl mt-1">Section Intros</h2>
          <p className="mt-1 text-sm text-muted-foreground max-w-xl">
            Edit copy + video URL for the intro banners on Financial Tools, Databases, and Launch Blueprint. Changes go live instantly — no redeploy.
          </p>
        </div>
      </div>

      {/* Key picker */}
      <div className="grid md:grid-cols-3 gap-3 mb-6" data-testid="admin-intros-picker">
        {INTRO_KEYS.map((k) => {
          const hasOverride = !!overrides[k.key];
          const active = selected === k.key;
          const stats = videoStats[`${k.key}_intro`];
          return (
            <button
              key={k.key} type="button" onClick={() => setSelected(k.key)}
              data-testid={`admin-intros-pick-${k.key}`}
              className={[
                "text-left rounded-2xl border p-4 transition",
                active ? "border-primary bg-primary/5 shadow-sm" : "border-shell-border bg-white hover:border-primary/40",
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-sm">{k.label}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{k.hint}</p>
                </div>
                {hasOverride ? (
                  <span className="inline-flex items-center rounded-full bg-emerald-500/15 text-emerald-800 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]">
                    Custom
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-shell-border/40 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                    Default
                  </span>
                )}
              </div>
              {stats && stats.starts > 0 && (
                <div
                  className="mt-3 pt-3 border-t border-shell-border/50 flex items-center gap-3 text-[11px]"
                  data-testid={`admin-intros-stats-${k.key}`}
                >
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <span className="font-bold text-slate-900">{stats.starts}</span> started
                  </span>
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <span className="font-bold">{stats.completions}</span> watched
                  </span>
                  <span className="ml-auto rounded-full bg-emerald-500/10 text-emerald-800 px-2 py-0.5 font-bold">
                    {Math.round(stats.completion_rate * 100)}%
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid lg:grid-cols-[minmax(0,1fr),minmax(0,1fr)] gap-6" data-testid="admin-intros-editor">
          {/* Form */}
          <div className="rounded-2xl border border-shell-border bg-white p-5 space-y-4">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Editor</p>
            <Field label="Eyebrow">
              <input type="text" value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })}
                data-testid="intro-field-eyebrow"
                placeholder={`e.g. ${currentMeta?.label}`}
                className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
            </Field>
            <Field label="Title">
              <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="intro-field-title"
                className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
            </Field>
            <Field label="Body">
              <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={4} data-testid="intro-field-body"
                className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
            </Field>
            <Field label="Bullets">
              <div className="space-y-2">
                {(form.bullets || []).map((b, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="text" value={b} onChange={(e) => setBullet(i, e.target.value)}
                      data-testid={`intro-field-bullet-${i}`}
                      className="flex-1 rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
                    <button type="button" onClick={() => rmBullet(i)} data-testid={`intro-field-bullet-remove-${i}`} className="rounded-full p-1.5 hover:bg-shell-border/40 transition">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addBullet} data-testid="intro-field-bullet-add" className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-3 py-1.5 text-[11px] font-semibold hover:border-primary/40 transition">
                  <Plus className="w-3 h-3" /> Add bullet
                </button>
              </div>
            </Field>
            <Field label="Video source" hint="Pick one: paste a video URL (YouTube / Vimeo / direct MP4), upload an MP4/WEBM/MOV, or paste the full Vimeo iframe embed. The chosen source replaces whatever was configured before.">
              <VideoSourcePicker
                /* Remount when the admin switches intros so the tab
                   state can't leak between them — belt-and-braces
                   alongside the value-driven `useEffect` in the
                   picker itself. */
                key={selected}
                value={form.videoUrl}
                onChange={(next) => setForm({ ...form, videoUrl: next })}
                uploadPathPrefix={`/api/intros/${selected}/asset/`}
                testIdPrefix="intro-field-video"
                uploadSlot={(
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept=".mp4,.webm,.mov,video/mp4,video/webm,video/quicktime"
                      onChange={onUploadVideo}
                      className="hidden"
                      data-testid="intro-video-file-input"
                    />
                    <button
                      type="button"
                      onClick={onPickVideo}
                      disabled={uploading}
                      data-testid="intro-video-upload"
                      className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-3 py-1.5 text-[11px] font-semibold hover:border-primary/40 transition disabled:opacity-60"
                    >
                      {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                      {uploading ? "Uploading…" : (form.videoUrl?.startsWith(`/api/intros/${selected}/asset/`) ? "Replace uploaded video" : "Upload video file")}
                    </button>
                    {form.videoUrl?.startsWith(`/api/intros/${selected}/asset/`) && (
                      <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-500/10 rounded-full px-2 py-0.5">
                        Uploaded file
                      </span>
                    )}
                  </div>
                )}
              />
            </Field>

            <div className="pt-2 flex items-center gap-2 flex-wrap">
              <button type="button" onClick={save} disabled={saving} data-testid="intro-save"
                className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-5 py-2.5 text-xs font-bold hover:bg-primary/90 disabled:opacity-60 transition">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {saving ? "Saving…" : "Save intro"}
              </button>
              {overrides[selected] && (
                <button type="button" onClick={reset} data-testid="intro-reset"
                  className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-4 py-2 text-[11px] font-semibold hover:border-primary/40 transition">
                  <RefreshCw className="w-3 h-3" /> Reset to default
                </button>
              )}
              <a href={currentMeta?.path} target="_blank" rel="noreferrer" data-testid="intro-open-live"
                className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-4 py-2 text-[11px] font-semibold hover:border-primary/40 transition ml-auto">
                Open live <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* Live preview */}
          <div className="rounded-2xl border border-shell-border bg-shell-border/10 p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Live preview</p>
            <div className="pointer-events-none scale-[0.85] origin-top-left w-[117.6%]">
              <IntroSection
                {...form}
                ctaLabel="Sample CTA"
                ctaHref="#"
                testId="intro-preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.14em] font-bold text-muted-foreground">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// Walkthroughs — admin editor for section-tour steps
// ═══════════════════════════════════════════════════════════════════════

// Kept in sync with SECTION_TOURS default keys in GuidedTour.jsx so admins
// see every section in the picker even if no override exists yet.
const WALKTHROUGH_KEYS = [
  { id: "dashboard", label: "Dashboard", path: "/dashboard" },
  { id: "welcome", label: "Welcome", path: "/welcome" },
  { id: "course", label: "Course", path: "/course" },
  { id: "progress", label: "Progress", path: "/progress" },
  { id: "exam", label: "Exam", path: "/exam/final" },
  { id: "blueprint", label: "Blueprint", path: "/blueprint" },
  { id: "assistant", label: "AI Assistant", path: "/assistant" },
  { id: "members", label: "Members", path: "/members" },
  { id: "databases", label: "Databases", path: "/databases" },
  { id: "tools", label: "Financial Tools", path: "/tools" },
  { id: "ai-cpg-tools", label: "AI CPG Tools", path: "/ai-cpg-tools" },
  { id: "agreements", label: "CPG Agreements", path: "/agreements" },
  { id: "planner", label: "Planner", path: "/planner" },
  { id: "downloads", label: "Downloads", path: "/downloads" },
  { id: "account", label: "Account", path: "/account" },
  { id: "admin", label: "Admin Console", path: "/admin" },
];

const BLANK_TOUR = { label: "", blurb: "", path: "", steps: [] };
const NEW_STEP = () => ({ id: crypto.randomUUID?.() || String(Date.now() + Math.random()), target: "", title: "", content: "", placement: "bottom" });
const _STEP_ID = () => (crypto.randomUUID?.() || String(Date.now() + Math.random()));

// Pull the hardcoded default steps for a section (if any) and shape them
// to match the editor's step schema — each step needs a stable `id` so
// drag/reorder/remove controls work. Returns [] when the section has
// no hardcoded default.
const seedStepsFromDefaults = (sectionId) => {
  const base = SECTION_TOURS?.[sectionId];
  if (!base || !Array.isArray(base.steps)) return [];
  return base.steps.map((s) => ({
    id: _STEP_ID(),
    target: s.target || "",
    title: s.title || "",
    content: s.content || "",
    placement: s.placement || "bottom",
  }));
};

// Cross-tab handshake key used by the picker overlay to hand back the selected
// selector to whichever step is currently in "pick mode".
const PICKER_CHANNEL = "cpg_tour_picker";

export function WalkthroughsConsole() {
  const [overrides, setOverrides] = useState({});
  const [analytics, setAnalytics] = useState({});
  const [selected, setSelected] = useState("dashboard");
  const [form, setForm] = useState(BLANK_TOUR);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickingStepId, setPickingStepId] = useState(null); // step.id currently listening for a picker result
  // Local reorder buffer — the picker rail lets admins drag rows and
  // then hit "Save order" to persist. `orderedKeys` seeds from the
  // merged supported+overrides list and mutates on drag events.
  const [orderedKeys, setOrderedKeys] = useState([]);
  const [orderDirty, setOrderDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  // Modal state for "Add sidebar menu" — lists sidebar nav items the
  // admin hasn't yet added a walkthrough for.
  const [showAddSidebar, setShowAddSidebar] = useState(false);
  // Modal state for the two-step delete confirmation.
  const [deleteKey, setDeleteKey] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/walkthroughs").then((r) => setOverrides(r.data?.overrides || {})).catch(() => {}),
      api.get("/admin/walkthroughs/analytics").then((r) => setAnalytics(r.data?.sections || {})).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    const doc = overrides[selected];
    const meta = WALKTHROUGH_KEYS.find((k) => k.id === selected);
    const base = SECTION_TOURS?.[selected];
    if (doc) {
      setForm({
        label: doc.label || base?.label || meta?.label || "",
        blurb: doc.blurb || base?.blurb || "",
        path: doc.path || base?.path || meta?.path || "",
        steps: (doc.steps || []).map((s) => ({ ...s, id: s.id || NEW_STEP().id })),
      });
    } else {
      // No DB override yet — seed the editor with the hardcoded default
      // steps so the admin sees exactly what founders currently see on
      // the live walkthrough. Save will then persist this as an override;
      // Reset deletes the override and this effect re-seeds on reload.
      setForm({
        label: base?.label || meta?.label || "",
        blurb: base?.blurb || "",
        path: base?.path || meta?.path || "",
        steps: seedStepsFromDefaults(selected),
      });
    }
  }, [selected, overrides]);

  // Listen for picker results — BroadcastChannel is one-way; storage event is
  // the reliable cross-tab fallback.
  useEffect(() => {
    if (!pickingStepId) return;
    let bc;
    const applySelector = (selector) => {
      setForm((f) => ({
        ...f,
        steps: (f.steps || []).map((s) => s.id === pickingStepId ? { ...s, target: selector } : s),
      }));
      setPickingStepId(null);
      toast.success(`Captured ${selector}`);
    };
    try {
      bc = new BroadcastChannel(PICKER_CHANNEL);
      bc.onmessage = (ev) => {
        const { stepId, selector } = ev.data || {};
        if (stepId === pickingStepId && selector) applySelector(selector);
      };
    } catch { /* older browsers */ }
    const onStorage = (e) => {
      if (e.key !== `${PICKER_CHANNEL}_${pickingStepId}`) return;
      if (!e.newValue) return;
      try {
        const payload = JSON.parse(e.newValue);
        if (payload?.selector) applySelector(payload.selector);
      } catch {}
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      if (bc) bc.close();
    };
  }, [pickingStepId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/walkthroughs/${selected}`, form);
      toast.success("Walkthrough saved");
      // Nudge live GuidedTour to refetch overrides
      window.dispatchEvent(new Event("cpg:tour:overrides-refreshed"));
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Delete this override and fall back to the hardcoded default?")) return;
    try {
      await api.post(`/admin/walkthroughs/${selected}/reset`);
      toast.success("Override cleared");
      window.dispatchEvent(new Event("cpg:tour:overrides-refreshed"));
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const setStep = (id, patch) => {
    setForm((f) => ({ ...f, steps: (f.steps || []).map((s) => s.id === id ? { ...s, ...patch } : s) }));
  };
  const addStep = () => setForm((f) => ({ ...f, steps: [...(f.steps || []), NEW_STEP()] }));
  const rmStep = (id) => setForm((f) => ({ ...f, steps: (f.steps || []).filter((s) => s.id !== id) }));
  const moveStep = (i, dir) => {
    setForm((f) => {
      const steps = [...(f.steps || [])];
      const j = i + dir;
      if (j < 0 || j >= steps.length) return f;
      [steps[i], steps[j]] = [steps[j], steps[i]];
      return { ...f, steps };
    });
  };

  const startPicker = (stepId) => {
    setPickingStepId(stepId);
    const target = form.path || WALKTHROUGH_KEYS.find((k) => k.id === selected)?.path || "/dashboard";
    const url = `${target}?_tourPicker=${encodeURIComponent(stepId)}`;
    window.open(url, `cpgPicker_${stepId}`, "width=1280,height=800");
    toast("Point at any element in the new tab to capture it.");
  };

  const currentMeta = WALKTHROUGH_KEYS.find((k) => k.id === selected);
  const overrideCount = useMemo(() => Object.keys(overrides || {}).length, [overrides]);

  // Merge WALKTHROUGH_KEYS (hardcoded defaults) with any custom entries
  // an admin has added via "Add Sidebar Menu". Custom entries come from
  // the overrides map with a `section_id` that isn't in WALKTHROUGH_KEYS.
  // We store a fallback label/path derived from the section_id so the
  // rail always has something to render.
  const allSections = useMemo(() => {
    const byId = {};
    for (const k of WALKTHROUGH_KEYS) byId[k.id] = { ...k, source: "default" };
    for (const [sid, o] of Object.entries(overrides || {})) {
      if (byId[sid]) continue;
      const label = o?.label || sid.replace(/[-_]/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
      const path = o?.path || `/${sid}`;
      byId[sid] = { id: sid, label, path, source: "custom" };
    }
    return byId;
  }, [overrides]);

  // Compute the initial rail order: honour admin-set `order` first,
  // then fall back to hardcoded WALKTHROUGH_KEYS order for anything
  // without a saved order. Filters out `deleted` rows entirely.
  useEffect(() => {
    if (loading) return;
    const alive = Object.entries(allSections).filter(([id]) => overrides[id]?.status !== "deleted");
    alive.sort(([aId, a], [bId, b]) => {
      const ao = overrides[aId]?.order;
      const bo = overrides[bId]?.order;
      if (Number.isInteger(ao) && Number.isInteger(bo)) return ao - bo;
      if (Number.isInteger(ao)) return -1;
      if (Number.isInteger(bo)) return 1;
      // Preserve hardcoded WALKTHROUGH_KEYS order for defaults; append customs.
      const ai = WALKTHROUGH_KEYS.findIndex((k) => k.id === aId);
      const bi = WALKTHROUGH_KEYS.findIndex((k) => k.id === bId);
      if (ai >= 0 && bi >= 0) return ai - bi;
      if (ai >= 0) return -1;
      if (bi >= 0) return 1;
      return aId.localeCompare(bId);
    });
    setOrderedKeys(alive.map(([id]) => id));
    setOrderDirty(false);
  }, [loading, overrides, allSections]);

  // Status flip — Show / Hide. Delete uses its own confirmation modal.
  const setStatus = async (sectionId, status) => {
    try {
      await api.patch(`/admin/walkthroughs/${sectionId}/status`, { status });
      const verb = status === "shown" ? "shown" : status === "hidden" ? "hidden" : "deleted";
      toast.success(`Walkthrough ${verb}`);
      window.dispatchEvent(new Event("cpg:tour:overrides-refreshed"));
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  // Persist the current rail order.
  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.patch("/admin/walkthroughs/order", { order: orderedKeys });
      toast.success("Order saved");
      setOrderDirty(false);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSavingOrder(false);
    }
  };

  // HTML5 native drag-drop — light, no library. `dragKey` remembers the
  // row picked up; `onDragOver` on a target reorders the array live so
  // the visual reflects the drop before release.
  const onDragStart = (id) => setDragKey(id);
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    if (!dragKey || dragKey === id) return;
    setOrderedKeys((keys) => {
      const from = keys.indexOf(dragKey);
      const to = keys.indexOf(id);
      if (from < 0 || to < 0) return keys;
      const copy = [...keys];
      copy.splice(from, 1);
      copy.splice(to, 0, dragKey);
      return copy;
    });
    setOrderDirty(true);
  };
  const onDragEnd = () => setDragKey(null);

  return (
    <div data-testid="admin-walkthroughs-console">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
            <Compass className="w-3 h-3" /> Guided tours
          </p>
          <h2 className="font-display text-2xl mt-1">Walkthroughs</h2>
          <p className="mt-1 text-sm text-muted-foreground max-w-xl">
            Rewrite step copy, retarget selectors, or reorder steps for any section tour. Changes go live instantly. Use <strong>Pick element</strong> to point at a UI element in a new tab and capture its selector automatically.
          </p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary border border-primary/30 px-2.5 py-1 text-[11px] font-semibold">
          {overrideCount} override{overrideCount === 1 ? "" : "s"}
        </span>
      </div>

      <div className="grid lg:grid-cols-[280px,minmax(0,1fr)] gap-5">
        {/* Section picker rail — drag-drop reorder + per-row Show/Hide
            /Delete controls. Every walkthrough in this list either has
            a hardcoded default or a custom sidebar-menu entry the admin
            added; deleted ones are filtered out and no longer appear
            in the runtime tour. */}
        <aside className="lg:sticky lg:top-4 h-fit rounded-2xl border border-shell-border bg-white p-2 max-h-[80vh] overflow-auto" data-testid="admin-walkthroughs-list">
          <div className="p-2 flex items-center justify-between gap-2 border-b border-shell-border mb-1">
            <p className="text-[11px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Walkthroughs</p>
            <button
              type="button"
              onClick={() => setShowAddSidebar(true)}
              data-testid="admin-walkthroughs-add"
              className="inline-flex items-center gap-1 rounded-full border border-shell-border px-2.5 py-1 text-[10px] font-semibold hover:border-primary/40 hover:text-primary transition"
            >
              <Plus className="w-3 h-3" /> Add sidebar menu
            </button>
          </div>
          {orderedKeys.map((keyId) => {
            const k = allSections[keyId];
            if (!k) return null;
            const o = overrides[k.id];
            const status = o?.status || "shown";
            const has = !!o && (o.steps?.length > 0 || o.label || o.blurb || o.path);
            const active = selected === k.id;
            const a = analytics[k.id];
            return (
              <div
                key={k.id}
                draggable
                onDragStart={() => onDragStart(k.id)}
                onDragOver={onDragOver(k.id)}
                onDragEnd={onDragEnd}
                data-testid={`admin-walkthroughs-row-${k.id}`}
                className={[
                  "group rounded-lg transition mb-0.5",
                  active ? "bg-primary/10" : "hover:bg-shell-border/30",
                  dragKey === k.id ? "opacity-40" : "",
                  status === "hidden" ? "opacity-70" : "",
                ].join(" ")}
              >
                <button
                  type="button"
                  onClick={() => setSelected(k.id)}
                  data-testid={`admin-walkthroughs-pick-${k.id}`}
                  className={[
                    "w-full text-left px-3 py-2 flex items-center gap-2",
                    active ? "text-primary font-semibold" : "",
                  ].join(" ")}
                >
                  <GripVertical className="w-3.5 h-3.5 text-muted-foreground cursor-grab active:cursor-grabbing shrink-0" />
                  <span className={["text-sm flex-1 min-w-0 truncate", status === "hidden" ? "line-through" : ""].join(" ")}>
                    {k.label}
                    {a?.starts > 0 && (
                      <span className="ml-1.5 text-[10px] text-muted-foreground font-normal" data-testid={`admin-walkthroughs-rail-stats-${k.id}`}>
                        · {a.starts} start{a.starts === 1 ? "" : "s"}
                      </span>
                    )}
                  </span>
                  {k.source === "custom" && (
                    <span className="text-[9px] font-bold uppercase tracking-[0.12em] rounded-full bg-sky-500/15 text-sky-800 border border-sky-500/30 px-1.5 py-0.5">
                      Custom
                    </span>
                  )}
                  {has && k.source === "default" && (
                    <span className="text-[9px] font-bold uppercase tracking-[0.12em] rounded-full bg-emerald-500/15 text-emerald-800 border border-emerald-500/30 px-1.5 py-0.5">
                      Custom
                    </span>
                  )}
                </button>
                {/* Show / Hide / Delete controls — inline so admins
                    don't need to enter the editor to flip visibility. */}
                <div className="px-3 pb-2 flex items-center gap-1" data-testid={`admin-walkthroughs-controls-${k.id}`}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setStatus(k.id, "shown"); }}
                    data-testid={`admin-walkthroughs-show-${k.id}`}
                    className={[
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border transition",
                      status === "shown" ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/40" : "border-shell-border text-muted-foreground hover:border-emerald-400 hover:text-emerald-700",
                    ].join(" ")}
                  >
                    <Eye className="w-3 h-3" /> Show
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setStatus(k.id, "hidden"); }}
                    data-testid={`admin-walkthroughs-hide-${k.id}`}
                    className={[
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border transition",
                      status === "hidden" ? "bg-pink-500/15 text-pink-700 border-pink-500/40" : "border-shell-border text-muted-foreground hover:border-pink-400 hover:text-pink-700",
                    ].join(" ")}
                  >
                    <EyeOff className="w-3 h-3" /> Hide
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setDeleteKey(k.id); }}
                    data-testid={`admin-walkthroughs-delete-${k.id}`}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border border-shell-border text-muted-foreground hover:border-rose-400 hover:text-rose-600 transition ml-auto"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
          {orderDirty && (
            <div className="p-2 border-t border-shell-border mt-1">
              <button
                type="button"
                onClick={saveOrder}
                disabled={savingOrder}
                data-testid="admin-walkthroughs-save-order"
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-white px-3 py-2 text-[11px] font-bold hover:bg-primary/90 disabled:opacity-60 transition"
              >
                {savingOrder ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                {savingOrder ? "Saving order…" : "Save order"}
              </button>
            </div>
          )}
        </aside>

        {/* Editor */}
        {loading ? (
          <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="rounded-2xl border border-shell-border bg-white p-5 space-y-5" data-testid="admin-walkthrough-editor">
            <AnalyticsSummary analytics={analytics[selected]} />

            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Label"><input type="text" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="tour-field-label" className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" /></Field>
              <Field label="Path"><input type="text" value={form.path} onChange={(e) => setForm({ ...form, path: e.target.value })} data-testid="tour-field-path" className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" /></Field>
              <Field label="Blurb" hint="One-liner shown on the /walkthrough card"><input type="text" value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} data-testid="tour-field-blurb" className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" /></Field>
            </div>

            <div>
              <div className="flex items-center justify-between gap-2 mb-3">
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-muted-foreground">Steps · {form.steps?.length || 0}</p>
                <button type="button" onClick={addStep} data-testid="tour-step-add" className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-3 py-1.5 text-[11px] font-semibold hover:border-primary/40 transition">
                  <Plus className="w-3 h-3" /> Add step
                </button>
              </div>
              <div className="space-y-3">
                {(form.steps || []).map((s, i) => {
                  const stepStats = (analytics[selected]?.steps || []).find((st) => st.index === i);
                  return (
                  <div key={s.id} className="rounded-xl border border-shell-border p-3" data-testid={`tour-step-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex flex-col">
                        <button type="button" onClick={() => moveStep(i, -1)} disabled={i === 0} data-testid={`tour-step-${i}-up`} className="p-0.5 disabled:opacity-30">▲</button>
                        <button type="button" onClick={() => moveStep(i, 1)} disabled={i === form.steps.length - 1} data-testid={`tour-step-${i}-down`} className="p-0.5 disabled:opacity-30">▼</button>
                      </div>
                      <span className="w-6 h-6 grid place-items-center rounded-full bg-primary/10 text-primary text-[11px] font-bold">{i + 1}</span>
                      <input type="text" value={s.title} onChange={(e) => setStep(s.id, { title: e.target.value })} data-testid={`tour-step-${i}-title`} placeholder="Step title" className="flex-1 rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
                      <StepDropoffPill stats={stepStats} testId={`tour-step-${i}-dropoff`} />
                      <button type="button" onClick={() => rmStep(s.id)} data-testid={`tour-step-${i}-remove`} className="rounded-full p-1.5 hover:bg-rose-500/10 text-rose-600 transition">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <textarea value={s.content} onChange={(e) => setStep(s.id, { content: e.target.value })} rows={2} data-testid={`tour-step-${i}-content`} placeholder="Bubble copy…" className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40 mt-1" />
                    <div className="grid md:grid-cols-[minmax(0,1fr),120px,auto] gap-2 mt-2 items-center">
                      <input type="text" value={s.target} onChange={(e) => setStep(s.id, { target: e.target.value })} data-testid={`tour-step-${i}-target`} placeholder="[data-testid='xxx']" className="rounded-lg border border-shell-border bg-white px-3 py-2 text-xs font-mono focus:outline-none focus:border-primary/40" />
                      {/* Step tooltip placement — decides which side of the
                          highlighted DOM target Joyride renders the coaching
                          bubble on. `auto` = Joyride picks the side with the
                          most room (safest default; use it when unsure).
                          `top/bottom/left/right` = pin to that side.
                          `center` = centered modal (use with `body` target). */}
                      <select
                        value={["auto","top","bottom","left","right","center"].includes(s.placement) ? s.placement : "auto"}
                        onChange={(e) => setStep(s.id, { placement: e.target.value })}
                        data-testid={`tour-step-${i}-placement`}
                        aria-label="Tooltip placement"
                        title="Where should the coaching bubble sit relative to the highlighted element?"
                        className="rounded-lg border border-shell-border bg-white px-2 py-2 text-xs focus:outline-none focus:border-primary/40"
                      >
                        <option value="auto">auto</option>
                        <option value="top">top</option>
                        <option value="bottom">bottom</option>
                        <option value="left">left</option>
                        <option value="right">right</option>
                        <option value="center">center</option>
                      </select>
                      <button type="button" onClick={() => startPicker(s.id)} data-testid={`tour-step-${i}-pick`}
                        className={[
                          "inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[11px] font-semibold transition",
                          pickingStepId === s.id ? "bg-primary text-white" : "border border-shell-border hover:border-primary/40",
                        ].join(" ")}>
                        <TargetIcon className="w-3 h-3" /> {pickingStepId === s.id ? "Waiting…" : "Pick element"}
                      </button>
                    </div>
                  </div>
                  );
                })}
                {(form.steps || []).length === 0 && (
                  <p className="text-xs text-muted-foreground italic">No steps yet — add one to override the hardcoded default.</p>
                )}
              </div>
            </div>

            <div className="pt-2 border-t border-shell-border flex items-center gap-2 flex-wrap">
              <button type="button" onClick={save} disabled={saving} data-testid="tour-save"
                className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-5 py-2.5 text-xs font-bold hover:bg-primary/90 disabled:opacity-60 transition">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {saving ? "Saving…" : "Save walkthrough"}
              </button>
              {overrides[selected] && (
                <button type="button" onClick={reset} data-testid="tour-reset"
                  className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-4 py-2 text-[11px] font-semibold hover:border-primary/40 transition">
                  <RefreshCw className="w-3 h-3" /> Reset to default
                </button>
              )}
              <a href={currentMeta?.path} target="_blank" rel="noreferrer" data-testid="tour-open-live"
                className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-4 py-2 text-[11px] font-semibold hover:border-primary/40 transition ml-auto">
                Open section <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Add Sidebar Menu — picker of sidebar entries without a
          walkthrough yet. On confirm we upsert an empty walkthrough
          shell so the row appears in the rail and the admin can then
          add steps. */}
      {showAddSidebar && (
        <AddSidebarMenuModal
          // Only sections that are CURRENTLY alive count as "taken".
          // A section soft-deleted via the walkthroughs rail (status =
          // "deleted") is still in `allSections`/`overrides` but must
          // reappear as an option here so an admin can re-add it. The
          // PUT /admin/walkthroughs/{id} handler upserts the row so
          // re-adding cleanly resurrects the previously-deleted entry.
          existingIds={new Set(
            [
              ...Object.keys(allSections),
              ...Object.keys(overrides || {}),
            ].filter((id) => overrides[id]?.status !== "deleted")
          )}
          onClose={() => setShowAddSidebar(false)}
          onAdded={(newId) => {
            setShowAddSidebar(false);
            setSelected(newId);
            load();
            // Broadcast so the /walkthrough page + any open GuidedTour
            // instance re-fetch /walkthroughs and pick up the new
            // section without a hard reload.
            window.dispatchEvent(new Event("cpg:tour:overrides-refreshed"));
          }}
        />
      )}

      {/* Two-step delete — confirmation modal requiring typed DELETE
          to avoid a mis-click removing a whole tour from every
          founder's runtime. */}
      {deleteKey && (
        <DeleteWalkthroughModal
          sectionId={deleteKey}
          label={allSections[deleteKey]?.label || deleteKey}
          onClose={() => setDeleteKey(null)}
          onConfirmed={() => {
            setStatus(deleteKey, "deleted");
            setDeleteKey(null);
          }}
        />
      )}
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════
// Tour analytics UI (walkthroughs console)
// ═══════════════════════════════════════════════════════════════════════

function AnalyticsSummary({ analytics }) {
  const a = analytics || {};
  const starts = a.starts || 0;
  const finishes = a.finishes || 0;
  const rate = a.completion_rate ?? 0; // 0..1
  const ratePct = Math.round(rate * 100);
  const rateTone = rate >= 0.7 ? "emerald" : rate >= 0.4 ? "amber" : "rose";
  const rateBg = { emerald: "bg-emerald-500/15 text-emerald-800 border-emerald-500/30",
                   amber: "bg-pink-500/15 text-pink-800 border-pink-500/30",
                   rose: "bg-rose-500/15 text-rose-800 border-rose-500/30" }[rateTone];
  const worstStep = (a.steps || [])
    .filter((s) => s.views >= 3)                          // ignore noisy low-view steps
    .reduce((worst, s) => (!worst || s.drop_off_rate > worst.drop_off_rate ? s : worst), null);

  return (
    <div
      data-testid="admin-walkthrough-analytics"
      className="rounded-xl border border-shell-border bg-shell-border/10 p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-primary" />
        <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-muted-foreground">Drop-off analytics</p>
        {a.latest_event_at && (
          <span className="text-[10px] text-muted-foreground ml-auto">
            Last event {new Date(a.latest_event_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {starts === 0 ? (
        <p className="text-xs text-muted-foreground italic" data-testid="admin-walkthrough-analytics-empty">
          No events for this tour yet — launch it once from /walkthrough and stats will appear here.
        </p>
      ) : (
        <div className="grid sm:grid-cols-4 gap-3">
          <Stat label="Starts" value={starts} testid="admin-analytics-starts" />
          <Stat label="Finishes" value={finishes} testid="admin-analytics-finishes" />
          <Stat label="Founders" value={a.unique_founders || 0} testid="admin-analytics-founders" />
          <div className="rounded-lg bg-white border border-shell-border p-3">
            <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">Completion</p>
            <p className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 border text-[13px] font-semibold ${rateBg}`} data-testid="admin-analytics-completion-rate">
              {ratePct}%
            </p>
          </div>
        </div>
      )}

      {worstStep && (
        <div className="mt-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-900 px-3 py-2 flex items-center gap-2" data-testid="admin-analytics-worst-step">
          <TrendingDown className="w-4 h-4 shrink-0" />
          <p className="text-xs">
            <strong>Step {worstStep.index + 1}</strong> has the highest drop-off (
            <strong>{Math.round(worstStep.drop_off_rate * 100)}%</strong> exit rate on {worstStep.views} views).
            Consider rewriting the copy or retargeting.
          </p>
        </div>
      )}
    </div>
  );
}


function StepDropoffPill({ stats, testId }) {
  if (!stats || !stats.views) return null;
  const pct = Math.round(stats.drop_off_rate * 100);
  const tone = stats.drop_off_rate >= 0.4 ? "rose" :
               stats.drop_off_rate >= 0.2 ? "amber" : "emerald";
  const cls = {
    rose:    "bg-rose-500/15 text-rose-800 border-rose-500/30",
    amber:   "bg-pink-500/15 text-pink-800 border-pink-500/30",
    emerald: "bg-emerald-500/15 text-emerald-800 border-emerald-500/30",
  }[tone];
  return (
    <span
      data-testid={testId}
      title={`${stats.views} views · ${stats.exits} exits (${stats.skips} skips, ${stats.closes} closes)`}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${cls}`}
    >
      {pct}% drop
    </span>
  );
}


function Stat({ label, value, testid }) {
  return (
    <div className="rounded-lg bg-white border border-shell-border p-3">
      <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-lg leading-none" data-testid={testid}>{value}</p>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════
// AddSidebarMenuModal — pick a sidebar nav entry to attach a walkthrough
// to. Reads /api/menu-visibility so the choices reflect the exact set
// of items an admin can see in Menu Directory, minus anything that
// already has a walkthrough entry.
// ═══════════════════════════════════════════════════════════════════════
function AddSidebarMenuModal({ existingIds, onClose, onAdded }) {
  const [items, setItems] = useState([]);   // [{ id, label, path }]
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Fetch the menu layout the admin has published. The `/menu-visibility`
    // endpoint returns every registered item, plus a `disabled` set for
    // anything the admin has hidden in the Menu Directory. Sync with that
    // set so the Add Sidebar Menu picker only surfaces items that are
    // currently Show-toggled — matches the exact promise the user made
    // to their team ("hidden = not selectable").
    //
    // Runs in production identically to preview because the endpoint is
    // Mongo-backed: whatever the admin toggles off in the Menu Directory
    // instantly disappears here after a page reload.
    api.get("/menu-visibility").then((r) => {
      const layout = r.data?.payload || r.data || {};
      const rawItems = layout.items || [];
      const disabled = new Set(layout.disabled || []);
      const candidates = [];
      for (const it of rawItems) {
        // Only nav-* items in a sidebar section make sense as tour
        // targets; header + account-menu entries have their own UI.
        if (!(it.id || "").startsWith("nav-")) continue;
        // Skip anything the admin has hidden in the Menu Directory —
        // syncs the two admin surfaces so a founder can't be offered
        // a walkthrough for a menu entry they can't reach.
        if (disabled.has(it.id)) continue;
        const sectionId = it.id.replace(/^nav-/, "");
        if (existingIds.has(sectionId)) continue;
        candidates.push({
          id: sectionId,
          nav_id: it.id,
          label: it.label || sectionId,
          // /menu-visibility doesn't carry the per-item route path
          // (paths live in the frontend NAV_META map). Fall back to
          // `/${sectionId}` — admin can retype it in the editor.
          path: `/${sectionId}`,
        });
      }
      setItems(candidates);
    }).catch(() => setItems([]));
  }, [existingIds]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pick) return;
    const chosen = items.find((c) => c.id === pick);
    if (!chosen) return;
    setBusy(true);
    try {
      // Upsert an empty walkthrough shell so the row shows up in the
      // rail immediately. Admin then adds steps in the editor panel.
      await api.put(`/admin/walkthroughs/${chosen.id}`, {
        label: chosen.label,
        path: chosen.path,
        blurb: "",
        steps: [],
        status: "shown",
      });
      toast.success(`Added "${chosen.label}" — add steps now`);
      onAdded(chosen.id);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-shell-side/60 backdrop-blur-sm grid place-items-center p-4"
      data-testid="add-sidebar-menu-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-shell-border flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-primary font-semibold">Walkthroughs</p>
            <h3 className="font-display text-xl mt-0.5">Add sidebar menu</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Every sidebar menu already has a walkthrough entry. Toggle Show/Hide/Delete on the existing rows to manage them.
            </p>
          ) : (
            <>
              <p className="text-sm text-foreground">
                Pick which sidebar menu the new walkthrough will attach to. After adding, the section appears in the picker rail and you can start dropping in steps.
              </p>
              <label className="grid gap-1.5">
                <span className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">
                  Sidebar menu
                </span>
                <select
                  value={pick}
                  onChange={(e) => setPick(e.target.value)}
                  data-testid="add-sidebar-menu-select"
                  className="rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
                >
                  <option value="">— choose one —</option>
                  {items.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label} · {c.path}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-shell-border flex items-center justify-end gap-2 bg-muted/30">
          <button
            type="button" onClick={onClose}
            data-testid="add-sidebar-menu-cancel"
            className="text-xs font-semibold text-muted-foreground border border-shell-border rounded-full px-4 py-2 hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!pick || busy || items.length === 0}
            data-testid="add-sidebar-menu-confirm"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white text-sm font-semibold px-4 py-2 hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
            {busy ? "Adding…" : "Add & edit steps"}
          </button>
        </div>
      </form>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// DeleteWalkthroughModal — typed-DELETE confirmation. Delete flips the
// status to "deleted" server-side, which drops it from the runtime tour
// endpoint but preserves the doc in Mongo (so the row can be recovered
// by manually setting status=shown or via /reset).
// ═══════════════════════════════════════════════════════════════════════
function DeleteWalkthroughModal({ sectionId, label, onClose, onConfirmed }) {
  const [typed, setTyped] = useState("");
  const canDelete = typed.trim() === "DELETE";
  return (
    <div
      className="fixed inset-0 z-50 bg-shell-side/60 backdrop-blur-sm grid place-items-center p-4"
      data-testid="delete-walkthrough-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={(e) => { e.preventDefault(); if (canDelete) onConfirmed(); }}
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-shell-border flex items-start justify-between bg-rose-50/60">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-rose-600 font-semibold">Delete walkthrough</p>
              <h3 className="font-display text-xl mt-0.5">Remove "{label}"</h3>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-foreground">
            This removes the walkthrough for <span className="font-semibold">{label}</span> from the guided-tour grid immediately.
            Founders will no longer see this tour or the "Take the tour" button on that section.
          </p>
          <label className="grid gap-1.5">
            <span className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">
              Type <code className="text-rose-600 font-mono">DELETE</code> to confirm
            </span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="DELETE"
              autoFocus
              spellCheck={false}
              data-testid="delete-walkthrough-confirm-input"
              className="rounded-lg border-2 border-rose-300 px-3 py-2 text-sm font-mono uppercase tracking-widest focus:outline-none focus:border-rose-500"
            />
          </label>
        </div>
        <div className="px-6 py-4 border-t border-shell-border flex items-center justify-end gap-2 bg-muted/30">
          <button
            type="button" onClick={onClose}
            data-testid="delete-walkthrough-cancel"
            className="text-xs font-semibold text-muted-foreground border border-shell-border rounded-full px-4 py-2 hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canDelete}
            data-testid="delete-walkthrough-confirm"
            className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 transition"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete walkthrough
          </button>
        </div>
      </form>
    </div>
  );
}

